import {
	Accordion,
	AccordionButton,
	AccordionRegion,
	Divider,
	Skeleton,
	Switch,
} from '@audio-network/componentlibrary'
import { useTranslation } from 'next-i18next'
import { useFormContext } from 'react-hook-form'

import { useDownloadContext } from '../../DownloadOptionsModal.context'
import downloadOptionsStyles from '../DownloadOptions.module.css'
import { getAddAllMixFileCount } from '../DownloadOptions.utils'
import MixOptionGroup from '../MixOptionGroup'
import { useMixOptionsSection } from './MixOptionsSection.hooks'
import styles from './MixOptionsSection.module.css'

const MixOptionsSectionContent = (): JSX.Element => {
	const { downloadOptions, initialTracks, isLoading, modalId } =
		useDownloadContext()
	const { control, setValue } = useFormContext()
	const { t } = useTranslation('download')
	const {
		allMixesSelected,
		allMixTypesSelected,
		allMixVariationsSelected,
		canTailorMixOptions,
		handleMixTypesSelectAllClick,
		handleMixVariationsSelectAllClick,
		handleSwitchClick,
		hasMixTypeOptions,
		hasMixVariationOptions,
		mixTypeOptions,
		mixVariationOptions,
	} = useMixOptionsSection(control, setValue)
	const mixFileCount = getAddAllMixFileCount(
		initialTracks,
		downloadOptions?.mixes ?? []
	)

	return (
		<>
			<Switch
				checked={allMixesSelected}
				className={downloadOptionsStyles.switch}
				label={
					<span className={downloadOptionsStyles.label}>
						<span className={downloadOptionsStyles.labelDescription}>
							{t('add-all-track-mixes')}
						</span>
						{mixFileCount > 0 && (
							<>
								{' '}
								<span className={downloadOptionsStyles.meta}>
									{mixFileCount}
								</span>
							</>
						)}
					</span>
				}
				// We use onClick here rather than onChange because we want to toggle the state immediately rather than waiting for an element change
				onClick={handleSwitchClick}
			/>
			{canTailorMixOptions && (
				<>
					<Divider className={downloadOptionsStyles.divider} />
					<Accordion
						className={downloadOptionsStyles.accordion}
						defaultRegionId={null}
					>
						<AccordionButton
							className={downloadOptionsStyles.accordionButton}
							controls={`${modalId}-mix-options`}
						>
							{t('select-mixes')}
						</AccordionButton>
						<AccordionRegion id={`${modalId}-mix-options`}>
							{isLoading && (
								<div
									aria-busy="true"
									aria-label={t('loading-download-options')}
									className={styles.loading}
									role="status"
								>
									<Skeleton width={280} />
									<Skeleton width={240} />
									<Skeleton width={280} />
									<Skeleton width={160} />
								</div>
							)}
							{downloadOptions && !isLoading && (
								<>
									{hasMixVariationOptions && (
										<MixOptionGroup
											allSelected={allMixVariationsSelected}
											modalId={modalId}
											onSelectAllClick={handleMixVariationsSelectAllClick}
											options={mixVariationOptions}
											selectAllButtonId={`${modalId}-mixes-select-all`}
											title={t('mixes')}
										/>
									)}
									{hasMixTypeOptions && (
										<MixOptionGroup
											allSelected={allMixTypesSelected}
											modalId={modalId}
											onSelectAllClick={handleMixTypesSelectAllClick}
											options={mixTypeOptions}
											selectAllButtonId={`${modalId}-cutdowns-select-all`}
											title={t('cutdowns')}
										/>
									)}
								</>
							)}
						</AccordionRegion>
					</Accordion>
				</>
			)}
		</>
	)
}

export default MixOptionsSectionContent
